import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, ShoppingBag, Users, DollarSign, Clock, Settings, MessageCircle, Store, Repeat, UserCog, Briefcase, Link2, Check, BarChart3, Tag, UserPlus, Film } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = window.location.origin + '/api';

export default function AdminDashboard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const isStoreAdmin = user?.role === 'store_admin';
  const [stats, setStats] = useState(null);
  const [supermarkets, setSupermarkets] = useState([]);
  const [recurringHealth, setRecurringHealth] = useState(null);
  const [processingCron, setProcessingCron] = useState(false);
  const [kitLinkCopied, setKitLinkCopied] = useState(false);
  const [unreadLeads, setUnreadLeads] = useState(0);
  const navigate = useNavigate();

  const copyKitLink = async () => {
    const url = `${window.location.origin}/merchant-kit`;
    try {
      await navigator.clipboard.writeText(url);
      setKitLinkCopied(true);
      setTimeout(() => setKitLinkCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      alert(`Copy failed — link is: ${url}`);
    }
  };

  const fetchRecurringHealth = () => {
    if (!isSuperAdmin) return;
    axios.get(`${API}/admin/recurring-orders/health`, { withCredentials: true })
      .then(r => setRecurringHealth(r.data))
      .catch(err => console.error('Failed to load recurring health:', err));
  };

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(err => console.error('Failed to load admin stats:', err));
    axios.get(`${API}/admin/supermarkets`, { withCredentials: true })
      .then(r => setSupermarkets(r.data))
      .catch(err => console.error('Failed to load supermarkets:', err));
    fetchRecurringHealth();
  }, [isSuperAdmin]);

  // Poll unread merchant leads every 30s (super admin only) and flash tab title when new leads arrive
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let prev = 0;
    const originalTitle = document.title;
    let flashInterval = null;

    const stopFlash = () => {
      if (flashInterval) {
        clearInterval(flashInterval);
        flashInterval = null;
        document.title = originalTitle;
      }
    };

    const startFlash = (count) => {
      stopFlash();
      let toggle = false;
      flashInterval = setInterval(() => {
        document.title = toggle ? originalTitle : `(${count}) New merchant lead 🔔`;
        toggle = !toggle;
      }, 1200);
    };

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/merchant-kit/admin/leads/unread-count`, { withCredentials: true });
        const count = data?.count || 0;
        setUnreadLeads(count);
        if (count > prev && count > 0) startFlash(count);
        if (count === 0) stopFlash();
        prev = count;
      } catch (err) {
        // silently ignore
      }
    };

    poll();
    const interval = setInterval(poll, 30000);
    const onVisibility = () => { if (!document.hidden) stopFlash(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      stopFlash();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isSuperAdmin]);

  const runRecurringCron = async () => {
    if (!window.confirm('Process all due recurring orders now? This will create new orders for active subscriptions whose next delivery date has arrived.')) return;
    setProcessingCron(true);
    try {
      const { data } = await axios.post(`${API}/admin/recurring-orders/process`, {}, { withCredentials: true });
      alert(`Processed ${data.processed} recurring order${data.processed === 1 ? '' : 's'}. ${data.errors.length > 0 ? `\nErrors: ${data.errors.length}` : ''}`);
      fetchRecurringHealth();
    } catch (err) {
      console.error('Recurring cron failed:', err);
      alert('Could not process recurring orders');
    }
    setProcessingCron(false);
  };

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <button className="back-btn" onClick={() => navigate('/')}>
        <ArrowLeft /> Home
      </button>
      <h2 data-testid="admin-dashboard-title">
        {isStoreAdmin ? `${user?.assigned_supermarket_name || 'Store'} Admin Dashboard` : 'Admin Dashboard'}
      </h2>
      {isStoreAdmin && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '-8px 0 16px' }} data-testid="store-admin-banner">
          You&apos;re managing <strong style={{ color: 'var(--navy)' }}>{user?.assigned_supermarket_name}</strong>. Catalog & orders are scoped to this supermarket only.
        </p>
      )}

      {/* Quick Navigation */}
      <div className="admin-nav" data-testid="admin-nav">
        <Link to="/admin/fulfillment" data-testid="admin-fulfillment-link" style={{ background: 'var(--lime)' }}>Fulfillment Queue</Link>
        <Link to="/admin/products" data-testid="admin-products-link">All Products</Link>
        <Link to="/admin/orders" data-testid="admin-orders-link">Manage Orders</Link>
        <button onClick={() => navigate('/admin/promos')} data-testid="admin-promos-btn"
          style={{ padding: '10px 20px', background: '#DB2777', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Film size={16} /> Promos
        </button>
        {isSuperAdmin && (
          <>
            <Link to="/admin/store-admins" data-testid="admin-store-admins-link"
              style={{ padding: '10px 20px', background: '#7C3AED', color: 'white', borderRadius: '10px', textDecoration: 'none', fontWeight: 500, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <UserCog size={16} /> Store Admins
            </Link>
            <button onClick={runRecurringCron} disabled={processingCron} data-testid="run-recurring-cron-btn"
              style={{ padding: '10px 20px', background: '#6366F1', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: processingCron ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px', opacity: processingCron ? 0.6 : 1 }}>
              <Repeat size={16} /> {processingCron ? 'Processing...' : 'Process Recurring'}
            </button>
            <button onClick={() => navigate('/admin/settings')} data-testid="admin-settings-btn"
              style={{ padding: '10px 20px', background: 'var(--navy)', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Settings size={16} /> Store Settings
            </button>
            <button onClick={() => navigate('/admin/coupons')} data-testid="admin-coupons-btn"
              style={{ padding: '10px 20px', background: '#059669', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Tag size={16} /> Coupons
            </button>
            <button onClick={() => navigate('/admin/branches')} data-testid="admin-branches-btn"
              style={{ padding: '10px 20px', background: '#0369A1', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Store size={16} /> Branches
            </button>
            <button onClick={() => navigate('/admin/invites')} data-testid="admin-invites-btn"
              style={{ padding: '10px 20px', background: '#7C3AED', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <UserPlus size={16} /> Invites
            </button>
            <button onClick={() => navigate('/admin/billing')} data-testid="admin-billing-btn"
              style={{ padding: '10px 20px', background: '#7C3AED', color: 'white', borderRadius: '10px', border: 'none', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <DollarSign size={16} /> Billing
            </button>
          </>
        )}
      </div>

      {/* Merchant Kit (Super Admin only) */}
      {isSuperAdmin && (
        <div
          data-testid="merchant-kit-card"
          style={{
            background: 'linear-gradient(135deg, var(--navy) 0%, #1e3a8a 100%)',
            color: 'white', borderRadius: '14px', padding: '18px 22px', marginBottom: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
            <div style={{ background: 'var(--lime)', color: 'var(--navy)', width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Briefcase size={22} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2 }}>Merchant Kit</div>
              <div style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>
                Share with supermarket owners. 5 docs: terms, pitch, WhatsApp scripts, FAQ, admin quick-start.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={() => navigate('/merchant-kit')}
              data-testid="merchant-kit-open-btn"
              style={{ padding: '9px 14px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              Open kit
            </button>
            <button
              onClick={() => navigate('/admin/leads')}
              data-testid="merchant-kit-leads-btn"
              style={{ position: 'relative', padding: '9px 14px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              Leads
              {unreadLeads > 0 && (
                <span
                  data-testid="unread-leads-badge"
                  style={{ background: '#ef4444', color: 'white', fontSize: '0.7rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999, minWidth: 18, textAlign: 'center', boxShadow: '0 0 0 2px var(--navy)' }}
                >
                  {unreadLeads}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/admin/kit-analytics')}
              data-testid="merchant-kit-analytics-btn"
              style={{ padding: '9px 14px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <BarChart3 size={14} /> Analytics
            </button>
            <button
              onClick={copyKitLink}
              data-testid="merchant-kit-copy-btn"
              style={{ padding: '9px 14px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              {kitLinkCopied ? <Check size={14} /> : <Link2 size={14} />}
              {kitLinkCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      )}

      {/* Recurring Orders Health */}
      {recurringHealth && (
        <div style={{ background: 'var(--white)', border: '1px solid var(--border-gray)', borderRadius: '14px', padding: '16px 20px', marginBottom: '24px' }} data-testid="recurring-health-widget">
          <h3 style={{ color: 'var(--navy)', fontSize: '1rem', margin: '0 0 12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Repeat size={18} /> Recurring Orders Health
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
            <div style={{ padding: '12px', borderRadius: '10px', background: '#EEF2FF', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#4338CA' }} data-testid="health-active-subs">{recurringHealth.active_subscriptions}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Active Subscriptions</div>
            </div>
            <div style={{ padding: '12px', borderRadius: '10px', background: recurringHealth.due_today_or_overdue > 0 ? '#FEF3C7' : '#F0FDF4', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: recurringHealth.due_today_or_overdue > 0 ? '#B45309' : '#15803D' }} data-testid="health-due-today">{recurringHealth.due_today_or_overdue}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Due Today / Overdue</div>
            </div>
            <div style={{ padding: '12px', borderRadius: '10px', background: '#F0F9FF', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0369A1' }} data-testid="health-due-tomorrow">{recurringHealth.due_tomorrow}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Due Tomorrow</div>
            </div>
            <div style={{ padding: '12px', borderRadius: '10px', background: '#F0FDF4', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#15803D' }} data-testid="health-processed-today">{recurringHealth.processed_today}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Auto-Orders Today</div>
            </div>
            <div style={{ padding: '12px', borderRadius: '10px', background: '#F9FAFB', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-muted)' }} data-testid="health-paused-subs">{recurringHealth.paused_subscriptions}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Paused</div>
            </div>
          </div>
        </div>
      )}

      {/* Supermarket Catalog Cards */}
      {supermarkets.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: 'var(--navy)', fontSize: '1rem', margin: '0 0 12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Store size={18} /> Supermarket Catalogs
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
            {supermarkets.map(s => {
              const productCount = stats ? Math.round(stats.total_products / supermarkets.length) : '...';
              return (
                <Link
                  key={s.slug}
                  to={`/admin/products?store=${s.slug}`}
                  data-testid={`catalog-link-${s.slug}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '16px',
                    background: 'var(--white)', border: '2px solid var(--border-gray)', borderRadius: '14px',
                    textDecoration: 'none', transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s'
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = s.color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-gray)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: 52, height: 52, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: '#f5f5f5' }}>
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Store size={24} color="white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{s.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Manage catalog &rarr;</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Store Settings — now a dedicated page at /admin/settings */}

      {/* Stats Grid */}
      {stats && (
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stat-card">
            <Package size={28} style={{ color: 'var(--navy)', marginBottom: '8px' }} />
            <div className="stat-value" data-testid="stat-products">{stats.total_products}</div>
            <div className="stat-label">Total Products</div>
          </div>
          <div className="stat-card">
            <ShoppingBag size={28} style={{ color: 'var(--lime)', marginBottom: '8px' }} />
            <div className="stat-value" data-testid="stat-orders">{stats.total_orders}</div>
            <div className="stat-label">Total Orders</div>
          </div>
          <div className="stat-card" data-testid="stat-users-card" style={{ display: isSuperAdmin ? undefined : 'none' }}>
            <Users size={28} style={{ color: '#6366F1', marginBottom: '8px' }} />
            <div className="stat-value" data-testid="stat-users">{stats.total_users ?? '—'}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <Clock size={28} style={{ color: 'var(--warning)', marginBottom: '8px' }} />
            <div className="stat-value" data-testid="stat-pending">{stats.pending_orders}</div>
            <div className="stat-label">Pending Orders</div>
          </div>
          <div className="stat-card">
            <DollarSign size={28} style={{ color: 'var(--success)', marginBottom: '8px' }} />
            <div className="stat-value" data-testid="stat-revenue">{stats.total_revenue.toFixed(2)}</div>
            <div className="stat-label">Revenue (PGK)</div>
          </div>
        </div>
      )}
    </div>
  );
}
